// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import Keybind, {type KeybindCommand} from '@app/features/input/state/InputKeybind';
import {resolveKeybindCommand} from '@app/features/input/state/input_keybind/KeybindCommands';
import {isBuiltinDisableMarker} from '@app/features/input/state/KeybindResolution';
import {combosLooseEqual} from '@app/features/input/utils/KeybindComboUtils';
import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import {RetryIcon} from '@app/features/ui/action_menu/ContextMenuIcons';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import styles from '@app/features/user/components/modals/tabs/KeybindsTab.module.css';
import {getCustomKeybindActionLabelMap} from '@app/features/user/components/modals/tabs/keybinds_tab/AssignableActionOptions';
import {CustomKeybindRow} from '@app/features/user/components/modals/tabs/keybinds_tab/CustomKeybindRow';
import {comboMatchesQuery, normalizeQuery} from '@app/features/user/components/modals/tabs/keybinds_tab/shared';
import {
	CUSTOM_SHORTCUTS_DESCRIPTOR,
	RESET_TO_DEFAULTS_DESCRIPTOR,
	SYNC_SHORTCUTS_DESCRIPTOR as SYNC_SHORTCUTS_ACROSS_DEVICES_DESCRIPTOR,
} from '@app/features/user/utils/UserMessageDescriptors';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {PlusIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useMemo} from 'react';

const UNASSIGNED_2_DESCRIPTOR = msg({
	message: '(unassigned)',
	comment: 'Short label in the keybinds tab. Keep it concise.',
});
const RESET_ALL_SHORTCUTS_DESCRIPTOR = msg({
	message: 'Reset all keybinds?',
	comment: 'Confirmation prompt in the keybinds tab.',
});
const THIS_REMOVES_EVERY_CUSTOM_SHORTCUT_AND_RE_ENABLES_DESCRIPTOR = msg({
	message: 'This removes every custom keybind and re-enables all built-in keybinds. This cannot be undone.',
	comment: 'Error message in the keybinds tab. Keep the tone plain and specific.',
});

const DISABLE_BUILT_IN_SHORTCUTS_DESCRIPTOR = msg({
	message: 'Disable built-ins',
	comment: 'Button or menu action label in the keybinds tab. Keep it concise.',
});

const SHORTCUTS_ARE_PAUSED_WHILE_RECORDING_DESCRIPTOR = msg({
	message: 'Keybinds are paused while recording.',
	comment: 'Short helper text in the keybinds tab.',
});
function resolveActionLabel(labelByAction: ReadonlyMap<KeybindCommand, string>, action: string | null): string | null {
	const resolved = resolveKeybindCommand(action);
	if (resolved === null) {
		return action;
	}
	return labelByAction.get(resolved) ?? resolved;
}

export const CustomKeybindsList: React.FC<{searchQuery: string}> = observer(({searchQuery}) => {
	const {i18n} = useLingui();
	const allCustomKeybinds = Keybind.getCustomKeybinds();
	const customKeybinds = useMemo(
		() => allCustomKeybinds.filter((entry) => !isBuiltinDisableMarker(entry)),
		[allCustomKeybinds],
	);
	const syncAcrossDevices = Keybind.getSyncAcrossDevices();
	const disableBuiltinKeybinds = Keybind.getDisableBuiltinKeybinds();
	const labelByAction = getCustomKeybindActionLabelMap(i18n, Keybind.getDefaults());
	const normalized = normalizeQuery(searchQuery);
	const filteredKeybinds = useMemo(() => {
		if (!normalized) return customKeybinds;
		return customKeybinds.filter((entry) => {
			const label = resolveActionLabel(labelByAction, entry.action) ?? '';
			if (label.toLowerCase().includes(normalized)) return true;
			return comboMatchesQuery(i18n, entry.combo, normalized);
		});
	}, [customKeybinds, labelByAction, normalized, i18n, i18n.locale]);
	const conflictLabels = useMemo(() => {
		const result = new Map<string, string>();
		for (let i = 0; i < customKeybinds.length; i++) {
			const target = customKeybinds[i];
			if (!target.enabled || !target.combo.key) continue;
			for (let j = 0; j < customKeybinds.length; j++) {
				if (i === j) continue;
				const other = customKeybinds[j];
				if (!other.enabled || !other.combo.key) continue;
				if (combosLooseEqual(target.combo, other.combo)) {
					const otherLabel = resolveActionLabel(labelByAction, other.action) ?? i18n._(UNASSIGNED_2_DESCRIPTOR);
					result.set(target.id, otherLabel);
					break;
				}
			}
		}
		return result;
	}, [customKeybinds, labelByAction, i18n.locale]);
	const handleAdd = () => {
		Keybind.addCustomKeybind();
	};
	const handleResetAll = () => {
		ModalCommands.push(
			modal(() => (
				<ConfirmModal
					title={i18n._(RESET_ALL_SHORTCUTS_DESCRIPTOR)}
					description={i18n._(THIS_REMOVES_EVERY_CUSTOM_SHORTCUT_AND_RE_ENABLES_DESCRIPTOR)}
					primaryText={i18n._(RESET_TO_DEFAULTS_DESCRIPTOR)}
					primaryVariant="danger"
					onPrimary={() => {
						Keybind.resetCustomKeybinds();
						Keybind.setDisableBuiltinKeybinds(false);
					}}
					data-flx="user.keybinds-tab.handle-reset-all.confirm-modal"
				/>
			)),
		);
	};
	const hasAnyOverrides = allCustomKeybinds.length > 0 || disableBuiltinKeybinds;
	let customList: React.ReactNode;
	if (customKeybinds.length === 0) {
		customList = (
			<div className={styles.emptyState} data-flx="user.keybinds-tab.custom-keybinds-list.empty-state">
				<Trans>No custom keybinds yet.</Trans>
			</div>
		);
	} else if (filteredKeybinds.length === 0) {
		customList = (
			<div className={styles.emptyState} data-flx="user.keybinds-tab.custom-keybinds-list.empty-state--2">
				<Trans>No custom keybinds match that search.</Trans>
			</div>
		);
	} else {
		customList = filteredKeybinds.map((entry) => (
			<CustomKeybindRow
				key={entry.id}
				entry={entry}
				conflictLabel={conflictLabels.get(entry.id) ?? null}
				onRemove={() => Keybind.removeCustomKeybind(entry.id)}
				data-flx="user.keybinds-tab.custom-keybinds-list.custom-keybind-row"
			/>
		));
	}
	return (
		<div className={styles.customSection} data-flx="user.keybinds-tab.custom-keybinds-list.custom-section">
			<div className={styles.customHeader} data-flx="user.keybinds-tab.custom-keybinds-list.custom-header">
				<div className={styles.customHeaderText} data-flx="user.keybinds-tab.custom-keybinds-list.custom-header-text">
					<h3 className={styles.customTitle} data-flx="user.keybinds-tab.custom-keybinds-list.custom-title">
						{i18n._(CUSTOM_SHORTCUTS_DESCRIPTOR)}
					</h3>
					<p className={styles.customSubtitle} data-flx="user.keybinds-tab.custom-keybinds-list.custom-subtitle">
						{i18n._(SHORTCUTS_ARE_PAUSED_WHILE_RECORDING_DESCRIPTOR)}
					</p>
				</div>
				<div
					className={styles.customHeaderActions}
					data-flx="user.keybinds-tab.custom-keybinds-list.custom-header-actions"
				>
					{hasAnyOverrides ? (
						<Button
							variant="secondary"
							small
							type="button"
							onClick={handleResetAll}
							leftIcon={<RetryIcon size={14} data-flx="user.keybinds-tab.custom-keybinds-list.retry-icon" />}
							data-flx="user.keybinds-tab.custom-keybinds-list.button.reset-all"
						>
							<Trans>Reset to defaults</Trans>
						</Button>
					) : null}
					<Button
						variant="primary"
						small
						type="button"
						onClick={handleAdd}
						leftIcon={
							<PlusIcon
								size={remFromPx(14)}
								weight="bold"
								data-flx="user.keybinds-tab.custom-keybinds-list.plus-icon"
							/>
						}
						data-flx="user.keybinds-tab.custom-keybinds-list.button.add"
					>
						<Trans>Add keybind</Trans>
					</Button>
				</div>
			</div>
			<div className={styles.shortcutOptions} data-flx="user.keybinds-tab.custom-keybinds-list.shortcut-options">
				<Switch
					className={styles.shortcutOption}
					label={i18n._(DISABLE_BUILT_IN_SHORTCUTS_DESCRIPTOR)}
					value={disableBuiltinKeybinds}
					onChange={(value) => Keybind.setDisableBuiltinKeybinds(value)}
					compact
					data-flx="user.keybinds-tab.custom-keybinds-list.switch.set-disable-builtin-keybinds"
				/>
				<Switch
					className={styles.shortcutOption}
					label={i18n._(SYNC_SHORTCUTS_ACROSS_DEVICES_DESCRIPTOR)}
					value={syncAcrossDevices}
					onChange={(value) => Keybind.setSyncAcrossDevices(value)}
					compact
					data-flx="user.keybinds-tab.custom-keybinds-list.switch.set-sync-across-devices"
				/>
			</div>
			<div className={styles.customList} data-flx="user.keybinds-tab.custom-keybinds-list.custom-list">
				{customList}
			</div>
		</div>
	);
});
